import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  { path: '/image-tasks/', name: 'image-tasks', component: () => import('@/pages/ImageTasksPage.vue'), meta: { requiresAuth: true, nav: 'Images' } },
  { path: '/image-tasks/new/', name: 'image-task-new', component: () => import('@/pages/ImageTaskNewPage.vue'), meta: { requiresAuth: true, nav: 'Images' } },
  { path: '/image-tasks/:id/', name: 'image-task', component: () => import('@/pages/ImageTaskPage.vue'), meta: { requiresAuth: true, nav: 'Images' } },
  {
    path: '/login/',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { requiresAuth: false, hideShell: true },
  },
  {
    path: '/',
    redirect: '/tasks/',
  },
  {
    path: '/tasks/',
    name: 'dashboard',
    component: () => import('@/pages/DashboardPage.vue'),
    meta: { nav: 'Tasks', requiresAuth: true },
  },
  {
    path: '/tasks/new/',
    name: 'task-new',
    component: () => import('@/pages/TaskNewPage.vue'),
    meta: { requiresAuth: true, hideNav: true },
  },
  {
    path: '/tasks/:id/upload/',
    name: 'task-upload',
    component: () => import('@/pages/TaskUploadPage.vue'),
    meta: { requiresAuth: true, hideNav: true },
  },
  {
    path: '/tasks/:id/mapping/',
    name: 'task-mapping',
    component: () => import('@/pages/TaskMappingPage.vue'),
    meta: { requiresAuth: true, hideNav: true },
  },
  {
    path: '/tasks/:id/checks/',
    name: 'task-checks',
    component: () => import('@/pages/TaskChecksPage.vue'),
    meta: { requiresAuth: true, hideNav: true },
  },
  {
    path: '/tasks/:id/process/',
    name: 'task-process',
    component: () => import('@/pages/TaskProcessPage.vue'),
    meta: { requiresAuth: true, nav: 'Process' },
  },
  {
    path: '/tasks/:id/rows/',
    name: 'task-rows',
    component: () => import('@/pages/TaskRowsPage.vue'),
    meta: { requiresAuth: true, nav: 'Rows' },
  },
  {
    path: '/rows/:id/',
    name: 'row-detail',
    component: () => import('@/pages/RowDetailPage.vue'),
    meta: { requiresAuth: true, hideNav: true },
  },
  {
    path: '/admin/providers/',
    name: 'admin-providers',
    component: () => import('@/pages/AdminProvidersPage.vue'),
    meta: { requiresAuth: true, nav: 'Providers', allowedRoles: ['admin'] },
  },
  {
    path: '/admin/stats/',
    name: 'admin-stats',
    component: () => import('@/pages/AdminStatsPage.vue'),
    meta: { requiresAuth: true, nav: 'Stats', allowedRoles: ['admin'] },
  },
  {
    path: '/admin/storages/',
    name: 'admin-storages',
    component: () => import('@/pages/AdminStoragesPage.vue'),
    meta: { requiresAuth: true, nav: 'Storages', allowedRoles: ['admin'] },
  },
  {
    path: '/admin/logs/',
    name: 'admin-logs',
    component: () => import('@/pages/AdminLogsPage.vue'),
    meta: { requiresAuth: true, nav: 'Logs', allowedRoles: ['admin'] },
  },
  {
    path: '/admin/validators/',
    name: 'admin-validators',
    component: () => import('@/pages/AdminValidatorsPage.vue'),
    meta: { requiresAuth: true, nav: 'Validators', allowedRoles: ['admin'] },
  },
  {
    path: '/admin/users/',
    name: 'admin-users',
    component: () => import('@/pages/AdminUsersPage.vue'),
    meta: { requiresAuth: true, nav: 'Users', allowedRoles: ['admin'] },
  },
  {
    path: '/admin/spam-formulas/',
    name: 'admin-spam-formulas',
    component: () => import('@/pages/AdminSpamFormulaPage.vue'),
    meta: { requiresAuth: true, nav: 'Spam formulas', allowedRoles: ['admin'] },
  },
  {
    path: '/admin/settings/',
    name: 'admin-settings',
    component: () => import('@/pages/AdminSettingsPage.vue'),
    meta: { requiresAuth: true, nav: 'Settings', allowedRoles: ['admin'] },
  },
  {
    path: '/profile/',
    name: 'profile',
    component: () => import('@/pages/ProfilePage.vue'),
    meta: { requiresAuth: true, hideNav: true },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/tasks/',
  },
]

export const router = createRouter({
  history: createWebHashHistory(),
  routes,
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath } }
  }
  if (!to.meta.requiresAuth && to.name !== 'login' && auth.isAuthenticated) {
    return { name: 'dashboard' }
  }
  const allowed = to.meta.allowedRoles as string[] | undefined
  if (allowed && auth.user) {
    // Django superusers always pass any role check; everyone else needs
    // an explicit match against their primary_role_slug.
    const isSuper = Boolean(auth.user.is_superuser)
    const slug = auth.user.primary_role_slug ?? ''
    if (!isSuper && !allowed.includes(slug)) {
      return { name: 'dashboard' }
    }
  }
  if (to.meta.requiresAuth && auth.isAuthenticated && !auth.user) {
    await auth.refreshUser()
  }
  return true
})
